import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { getSignedUrl, deleteFile } from '../../../lib/upload'

export const useContacts = () => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['contacts', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('household_id', household.id)
        .order('last_name')
        .order('first_name')
      if (error) throw error
      return data ?? []
    },
  })

  const create = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('contacts')
        .insert({ ...payload, household_id: household.id, created_by: user.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data, error } = await supabase
        .from('contacts')
        .update(payload)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const remove = useMutation({
    mutationFn: async ({ id, photoPath }) => {
      if (photoPath) await deleteFile('household-documents', photoPath).catch(() => {})
      const { error } = await supabase.from('contacts').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, create, update, remove }
}

export const useContactPhotoUrl = (photoPath) => {
  const key = ['contact-photo', photoPath]
  return useQuery({
    queryKey: key,
    enabled:  !!photoPath,
    queryFn:  () => getSignedUrl('household-documents', photoPath),
    staleTime: 1000 * 60 * 50,
  })
}
